"use client";

import { ReactNode, useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useRouter } from "@/i18n/navigation";
import { useAtomValue } from "jotai";
import { AuthControls } from "@components/ui/auth-controls";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/ui/card";
import { Spinner } from "@components/ui/spinner";
import { useAuth } from "@/frontend/contexts/auth-context";
import { userRoleAtom } from "@lib/atoms";
import { extractPathWithoutLocale, isLogoutRoute, isPublicRoute } from "@/frontend/config/routes";
import { hasBetaAccessRole } from "@/isomorphic/utils/system-role-access";

interface BetaAccessGuardProps {
  children: ReactNode;
}

export function BetaAccessGuard({ children }: BetaAccessGuardProps) {
  const t = useTranslations("betaAccess");
  const tLoading = useTranslations("loading");
  const { user, loading: authLoading, signOut } = useAuth();
  const userRole = useAtomValue(userRoleAtom);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    if (authLoading || !user || !pathname || userRole === null) {
      return;
    }

    if (isPublicRoute(pathname) || isLogoutRoute(pathname)) {
      return;
    }

    if (hasBetaAccessRole(userRole)) {
      return;
    }

    const pathWithoutLocale = extractPathWithoutLocale(pathname);
    if (pathWithoutLocale !== "/") {
      router.replace("/");
    }
  }, [authLoading, user, userRole, pathname, router]);

  if (!user || !pathname || isPublicRoute(pathname) || isLogoutRoute(pathname)) {
    return <>{children}</>;
  }

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <p className="text-muted-foreground text-sm">{tLoading("auth")}</p>
        </div>
      </div>
    );
  }

  if (userRole === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <p className="text-muted-foreground text-sm">{tLoading("auth")}</p>
        </div>
      </div>
    );
  }

  if (hasBetaAccessRole(userRole)) {
    return <>{children}</>;
  }

  if (extractPathWithoutLocale(pathname) !== "/") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <p className="text-muted-foreground text-sm">{tLoading("redirecting")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-muted/30 flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <div className="mb-4 flex justify-end">
          <AuthControls />
        </div>
        <Card>
          <CardHeader className="space-y-3">
            <CardTitle className="text-2xl">{t("title")}</CardTitle>
            <CardDescription>{t("message")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              onClick={async () => {
                setIsSigningOut(true);
                try {
                  await signOut();
                } finally {
                  setIsSigningOut(false);
                }
              }}
              disabled={isSigningOut}
              className="w-full"
            >
              {isSigningOut ? tLoading("logout") : t("signOut")}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
